import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { 
  Settings, 
  Mail, 
  Bell, 
  Shield, 
  Clock, 
  Palette,
  Database,
  RefreshCw,
  CheckCircle,
  XCircle,
  AlertCircle,
  Zap,
  Cloud,
  Trash2,
  Bot,
  Plane
} from "lucide-react";
import { EmailAccountSetup } from "./EmailAccountSetup";
import type { EmailAccount } from "@/types/email";

interface EmailSettingsProps {
  onClose: () => void;
}

interface SettingsData {
  autoSync: boolean;
  syncInterval: number;
  notifications: boolean;
  readReceipts: boolean;
  signatures: boolean;
  autoSave: boolean;
  theme: string;
  emailsPerPage: number;
  compactView: boolean;
  showPreview: boolean;
  // Autoresponder settings
  autoresponderEnabled: boolean;
  autoresponderMode: "ai" | "vacation";
  vacationMessage: string;
  excludeD2DDomains: boolean;
}

export const EmailSettings = ({ onClose }: EmailSettingsProps) => {
  const { user } = useAuth();
  const { toast } = useToast();
  
  const [accounts, setAccounts] = useState<EmailAccount[]>([]);
  const [settings, setSettings] = useState<SettingsData>({
    autoSync: true,
    syncInterval: 30,
    notifications: true,
    readReceipts: false,
    signatures: true,
    autoSave: true,
    theme: "system",
    emailsPerPage: 50,
    compactView: false,
    showPreview: true,
    autoresponderEnabled: false,
    autoresponderMode: "ai",
    vacationMessage: "vielen Dank für Ihre Nachricht. Ich bin derzeit nicht erreichbar und werde mich nach meiner Rückkehr bei Ihnen melden.",
    excludeD2DDomains: true,
  });
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState<string | null>(null);
  const [showAccountSetup, setShowAccountSetup] = useState(false);
  const [editingAccount, setEditingAccount] = useState<EmailAccount | null>(null);
  const [activeTab, setActiveTab] = useState("general");

  useEffect(() => {
    if (user) {
      loadEmailAccounts();
      loadSettings();
    }
  }, [user]);

  const loadEmailAccounts = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('email_accounts')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setAccounts((data || []) as EmailAccount[]);
    } catch (error) {
      console.error('Error loading email accounts:', error);
      toast({
        title: "Fehler",
        description: "E-Mail-Konten konnten nicht geladen werden.",
        variant: "destructive",
      });
    }
  };

  const loadSettings = async () => {
    // In einer echten App würdest du diese aus der Datenbank laden
    // Für jetzt verwenden wir localStorage
    const saved = localStorage.getItem('email-settings');
    if (saved) {
      setSettings(JSON.parse(saved));
    }
  };

  const saveSettings = async () => {
    setLoading(true);
    try {
      // Speichere in localStorage (in einer echten App in der Datenbank)
      localStorage.setItem('email-settings', JSON.stringify(settings));
      
      toast({
        title: "Einstellungen gespeichert",
        description: "Ihre E-Mail-Einstellungen wurden erfolgreich gespeichert.",
      });
    } catch (error) {
      console.error('Error saving settings:', error);
      toast({
        title: "Fehler",
        description: "Einstellungen konnten nicht gespeichert werden.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const testConnection = async (accountId: string) => {
    setTesting(accountId);
    try {
      const { data, error } = await supabase.functions.invoke('sync-emails', {
        body: { 
          accountId: accountId,
          testOnly: true 
        }
      });

      if (error) throw error;

      if (data.success) {
        toast({
          title: "Verbindung erfolgreich",
          description: "Die E-Mail-Server-Verbindung funktioniert einwandfrei.",
        });
      } else {
        throw new Error(data.error || "Verbindungstest fehlgeschlagen");
      }
    } catch (error) {
      console.error('Connection test failed:', error);
      toast({
        title: "Verbindung fehlgeschlagen",
        description: `Verbindungstest nicht erfolgreich: ${error instanceof Error ? error.message : 'Unbekannter Fehler'}`,
        variant: "destructive",
      });
    } finally {
      setTesting(null);
    }
  };

  const toggleAccountActive = async (accountId: string, isActive: boolean) => {
    try {
      const { error } = await supabase
        .from('email_accounts')
        .update({ is_active: isActive })
        .eq('id', accountId);

      if (error) throw error;

      setAccounts(prev => 
        prev.map(acc => 
          acc.id === accountId ? { ...acc, is_active: isActive } : acc
        )
      );

      toast({
        title: isActive ? "Konto aktiviert" : "Konto deaktiviert",
        description: `Das E-Mail-Konto wurde ${isActive ? 'aktiviert' : 'deaktiviert'}.`,
      });
    } catch (error) {
      console.error('Error updating account:', error);
      toast({
        title: "Fehler",
        description: "Kontostatus konnte nicht geändert werden.",
        variant: "destructive",
      });
    }
  };

  const getConnectionStatus = (account: EmailAccount) => {
    if (!account.is_active) {
      return { icon: XCircle, color: "text-gray-500", text: "Deaktiviert" };
    }
    if (!account.last_sync_at) {
      return { icon: AlertCircle, color: "text-yellow-500", text: "Nicht getestet" };
    }
    return { icon: CheckCircle, color: "text-green-500", text: "Verbunden" };
  };

  const formatLastSync = (lastSync?: string) => {
    if (!lastSync) return "Nie";
    const date = new Date(lastSync);
    const now = new Date();
    const diffMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
    
    if (diffMinutes < 1) return "Gerade eben";
    if (diffMinutes < 60) return `vor ${diffMinutes} Min`;
    if (diffMinutes < 1440) return `vor ${Math.floor(diffMinutes / 60)} Std`;
    return `vor ${Math.floor(diffMinutes / 1440)} Tagen`;
  };

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between border-b pb-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary/10 rounded-lg">
            <Settings className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">Einstellungen</h1>
            <p className="text-muted-foreground">
              Verwalten Sie Ihr E-Mail-Konto und Konfigurationen
            </p>
          </div>
        </div>
        <Button variant="outline" onClick={onClose}>
          Schließen
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-6">
          <TabsTrigger value="general" className="flex items-center gap-2">
            <Settings className="h-4 w-4" />
            Allgemein
          </TabsTrigger>
          <TabsTrigger value="accounts" className="flex items-center gap-2">
            <Mail className="h-4 w-4" />
            Konten
          </TabsTrigger>
          <TabsTrigger value="autoresponder" className="flex items-center gap-2">
            <Bot className="h-4 w-4" />
            Autoresponder
          </TabsTrigger>
          <TabsTrigger value="sync" className="flex items-center gap-2">
            <Cloud className="h-4 w-4" />
            Sync
          </TabsTrigger>
          <TabsTrigger value="notifications" className="flex items-center gap-2">
            <Bell className="h-4 w-4" />
            Benachrichtigungen
          </TabsTrigger>
          <TabsTrigger value="security" className="flex items-center gap-2">
            <Shield className="h-4 w-4" />
            Sicherheit
          </TabsTrigger>
        </TabsList>

        {/* Allgemeine Einstellungen */}
        <TabsContent value="general" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Palette className="h-5 w-5" />
                Darstellung
              </CardTitle>
              <CardDescription>
                Passen Sie das Aussehen des E-Mail-Clients an
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Design-Schema</Label>
                  <Select 
                    value={settings.theme} 
                    onValueChange={(value) => setSettings(prev => ({ ...prev, theme: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="light">Hell</SelectItem>
                      <SelectItem value="dark">Dunkel</SelectItem>
                      <SelectItem value="system">System</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>E-Mails pro Seite</Label>
                  <Select 
                    value={settings.emailsPerPage.toString()} 
                    onValueChange={(value) => setSettings(prev => ({ ...prev, emailsPerPage: parseInt(value) }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="25">25</SelectItem>
                      <SelectItem value="50">50</SelectItem>
                      <SelectItem value="100">100</SelectItem>
                      <SelectItem value="200">200</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Kompakte Ansicht</Label>
                  <p className="text-xs text-muted-foreground">
                    Zeigt mehr E-Mails auf weniger Platz
                  </p>
                </div>
                <Switch
                  checked={settings.compactView}
                  onCheckedChange={(checked) => setSettings(prev => ({ ...prev, compactView: checked }))}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>E-Mail-Vorschau anzeigen</Label>
                  <p className="text-xs text-muted-foreground">
                    Zeigt die ersten Zeilen des E-Mail-Inhalts
                  </p>
                </div>
                <Switch
                  checked={settings.showPreview}
                  onCheckedChange={(checked) => setSettings(prev => ({ ...prev, showPreview: checked }))}
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Zap className="h-5 w-5" />
                Verhalten
              </CardTitle>
              <CardDescription>
                Konfigurieren Sie das Verhalten des E-Mail-Clients
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Automatisches Speichern</Label>
                  <p className="text-xs text-muted-foreground">
                    Entwürfe werden automatisch gespeichert
                  </p>
                </div>
                <Switch
                  checked={settings.autoSave}
                  onCheckedChange={(checked) => setSettings(prev => ({ ...prev, autoSave: checked }))}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Signaturen verwenden</Label>
                  <p className="text-xs text-muted-foreground">
                    Signaturen automatisch zu neuen E-Mails hinzufügen
                  </p>
                </div>
                <Switch
                  checked={settings.signatures}
                  onCheckedChange={(checked) => setSettings(prev => ({ ...prev, signatures: checked }))}
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* E-Mail-Konto */}
        <TabsContent value="accounts" className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold">E-Mail-Konto</h3>
              <p className="text-sm text-muted-foreground">
                Verwalten Sie Ihr verbundenes E-Mail-Konto
              </p>
            </div>
            {accounts.length === 0 && (
              <Button onClick={() => setShowAccountSetup(true)} className="gap-2">
                <Mail className="h-4 w-4" />
                Konto verbinden
              </Button>
            )}
          </div>

          <div className="grid gap-4">
            {accounts.length === 0 ? (
              <Card>
                <CardContent className="text-center py-12">
                  <Mail className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                  <h3 className="text-lg font-medium mb-2">Kein E-Mail-Konto verbunden</h3>
                  <p className="text-muted-foreground mb-4">
                    Verbinden Sie Ihr E-Mail-Konto, um E-Mails zu senden und empfangen.
                  </p>
                  <Button onClick={() => setShowAccountSetup(true)}>
                    Konto verbinden
                  </Button>
                </CardContent>
              </Card>
            ) : (
              accounts.map((account) => {
                const status = getConnectionStatus(account);
                const StatusIcon = status.icon;
                
                return (
                  <Card key={account.id} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="p-3 bg-primary/10 rounded-lg">
                            <Mail className="h-6 w-6 text-primary" />
                          </div>
                          <div>
                            <h4 className="font-semibold text-lg">
                              {account.display_name || account.email_address}
                            </h4>
                            <p className="text-sm text-muted-foreground">
                              {account.email_address}
                            </p>
                            <div className="flex items-center gap-4 mt-2">
                              <div className="flex items-center gap-1">
                                <StatusIcon className={`h-4 w-4 ${status.color}`} />
                                <span className="text-sm">{status.text}</span>
                              </div>
                              <div className="text-xs text-muted-foreground">
                                Letzte Sync: {formatLastSync(account.last_sync_at)}
                              </div>
                              {account.is_default && (
                                <div className="bg-primary text-primary-foreground px-2 py-1 rounded text-xs">
                                  Standard
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-3">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => testConnection(account.id)}
                            disabled={testing === account.id}
                            className="gap-2"
                          >
                            <RefreshCw className={`h-4 w-4 ${testing === account.id ? 'animate-spin' : ''}`} />
                            Testen
                          </Button>
                          
                          <Button
                            variant="outline"
                            size="sm"
                            className="gap-2"
                            onClick={() => {
                              setEditingAccount(account);
                              setShowAccountSetup(true);
                            }}
                          >
                            <Settings className="h-4 w-4" />
                            Bearbeiten
                          </Button>
                          
                          <Button
                            variant="destructive"
                            size="sm"
                            className="gap-2"
                            onClick={async () => {
                              if (!confirm("E-Mail-Konto wirklich entfernen? Alle synchronisierten E-Mails werden gelöscht.")) return;
                              try {
                                await supabase.from('emails').delete().eq('account_id', account.id);
                                await supabase.from('email_accounts').delete().eq('id', account.id);
                                setAccounts([]);
                                toast({ title: "Entfernt", description: "E-Mail-Konto wurde entfernt" });
                              } catch (err) {
                                console.error(err);
                                toast({ title: "Fehler", description: "Konto konnte nicht entfernt werden", variant: "destructive" });
                              }
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                            Entfernen
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })
            )}
          </div>
        </TabsContent>

        {/* Autoresponder */}
        <TabsContent value="autoresponder" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bot className="h-5 w-5" />
                Autoresponder
              </CardTitle>
              <CardDescription>
                Automatische Antworten für eingehende E-Mails konfigurieren
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Autoresponder aktivieren</Label>
                  <p className="text-xs text-muted-foreground">
                    Automatisch auf eingehende E-Mails antworten
                  </p>
                </div>
                <Switch
                  checked={settings.autoresponderEnabled}
                  onCheckedChange={(checked) => setSettings(prev => ({ ...prev, autoresponderEnabled: checked }))}
                />
              </div>

              {settings.autoresponderEnabled && (
                <>
                  <div className="space-y-3">
                    <Label>Antwort-Modus</Label>
                    <div className="grid grid-cols-2 gap-4">
                      <div
                        className={`p-4 border rounded-lg cursor-pointer transition-all ${
                          settings.autoresponderMode === "ai" 
                            ? "border-primary bg-primary/5 ring-2 ring-primary/20" 
                            : "hover:border-muted-foreground/50"
                        }`}
                        onClick={() => setSettings(prev => ({ ...prev, autoresponderMode: "ai" }))}
                      >
                        <div className="flex items-center gap-3 mb-2">
                          <Bot className="h-5 w-5 text-primary" />
                          <span className="font-medium">KI-Autoresponder</span>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Personalisierte Antwort mit Namen und Bezug zum Anliegen. 
                          Z.B. "Guten Tag, Herr Müller. Vielen Dank für Ihre Nachricht zum Thema..."
                        </p>
                      </div>
                      <div
                        className={`p-4 border rounded-lg cursor-pointer transition-all ${
                          settings.autoresponderMode === "vacation" 
                            ? "border-primary bg-primary/5 ring-2 ring-primary/20" 
                            : "hover:border-muted-foreground/50"
                        }`}
                        onClick={() => setSettings(prev => ({ ...prev, autoresponderMode: "vacation" }))}
                      >
                        <div className="flex items-center gap-3 mb-2">
                          <Plane className="h-5 w-5 text-primary" />
                          <span className="font-medium">Abwesenheitsnotiz</span>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Feste Nachricht für alle eingehenden E-Mails, z.B. während des Urlaubs.
                        </p>
                      </div>
                    </div>
                  </div>

                  {settings.autoresponderMode === "vacation" && (
                  <div className="space-y-2">
                      <Label>Abwesenheitsnachricht</Label>
                      <Textarea
                        className="min-h-[120px] resize-none"
                        value={settings.vacationMessage}
                        onChange={(e) => setSettings(prev => ({ ...prev, vacationMessage: e.target.value }))}
                        placeholder="vielen Dank für Ihre Nachricht. Ich bin derzeit nicht erreichbar..."
                      />
                      <p className="text-xs text-muted-foreground">
                        Der Name des Absenders wird automatisch eingefügt, wenn verfügbar.
                      </p>
                    </div>
                  )}

                  {settings.autoresponderMode === "ai" && (
                    <div className="p-4 bg-muted/50 rounded-lg">
                      <div className="flex items-start gap-3">
                        <Bot className="h-5 w-5 text-primary mt-0.5" />
                        <div className="space-y-1">
                          <p className="text-sm font-medium">KI-generierte Antwort</p>
                          <p className="text-xs text-muted-foreground">
                            Die KI analysiert den Absendernamen und das Thema der E-Mail und erstellt 
                            eine persönliche Antwort wie: "Guten Tag, Herr Müller. Vielen Dank für 
                            Ihre Nachricht zum Thema Versicherungsanfrage. Ich werde mich in Kürze 
                            bei Ihnen melden."
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="flex items-center justify-between pt-4 border-t">
                    <div className="space-y-0.5">
                      <Label>D2D-Mails ausschließen</Label>
                      <p className="text-xs text-muted-foreground">
                        Keine automatischen Antworten an E-Mails vom D2D-System
                      </p>
                    </div>
                    <Switch
                      checked={settings.excludeD2DDomains}
                      onCheckedChange={(checked) => setSettings(prev => ({ ...prev, excludeD2DDomains: checked }))}
                    />
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Synchronisation */}
        <TabsContent value="sync" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5" />
                Automatische Synchronisation
              </CardTitle>
              <CardDescription>
                Konfigurieren Sie die automatische E-Mail-Synchronisation
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Automatische Synchronisation</Label>
                  <p className="text-xs text-muted-foreground">
                    E-Mails werden automatisch im Hintergrund synchronisiert
                  </p>
                </div>
                <Switch
                  checked={settings.autoSync}
                  onCheckedChange={(checked) => setSettings(prev => ({ ...prev, autoSync: checked }))}
                />
              </div>
              
              {settings.autoSync && (
                <div className="space-y-2">
                  <Label>Synchronisationsintervall (Sekunden)</Label>
                  <Select 
                    value={settings.syncInterval.toString()} 
                    onValueChange={(value) => setSettings(prev => ({ ...prev, syncInterval: parseInt(value) }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="15">15 Sekunden</SelectItem>
                      <SelectItem value="30">30 Sekunden</SelectItem>
                      <SelectItem value="60">1 Minute</SelectItem>
                      <SelectItem value="300">5 Minuten</SelectItem>
                      <SelectItem value="600">10 Minuten</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Benachrichtigungen */}
        <TabsContent value="notifications" className="space-y-6">
          <Card>
            <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bell className="h-5 w-5" />
              Benachrichtigungseinstellungen
              </CardTitle>
              <CardDescription>
                Verwalten Sie, wann und wie Sie benachrichtigt werden
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Desktop-Benachrichtigungen</Label>
                  <p className="text-xs text-muted-foreground">
                    Zeigt Benachrichtigungen für neue E-Mails
                  </p>
                </div>
                <Switch
                  checked={settings.notifications}
                  onCheckedChange={(checked) => setSettings(prev => ({ ...prev, notifications: checked }))}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Lesebestätigungen</Label>
                  <p className="text-xs text-muted-foreground">
                    Automatisch Lesebestätigungen senden
                  </p>
                </div>
                <Switch
                  checked={settings.readReceipts}
                  onCheckedChange={(checked) => setSettings(prev => ({ ...prev, readReceipts: checked }))}
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Sicherheit */}
        <TabsContent value="security" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5" />
                Sicherheitseinstellungen
              </CardTitle>
              <CardDescription>
                Schützen Sie Ihre E-Mails und Daten
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4">
                <div className="p-4 border rounded-lg">
                  <h4 className="font-medium mb-2">Verschlüsselung</h4>
                  <p className="text-sm text-muted-foreground mb-3">
                    Alle E-Mail-Verbindungen verwenden TLS/SSL-Verschlüsselung.
                  </p>
                  <div className="flex items-center gap-2 text-green-600">
                    <CheckCircle className="h-4 w-4" />
                    <span className="text-sm">Aktiv</span>
                  </div>
                </div>
                
                <div className="p-4 border rounded-lg">
                  <h4 className="font-medium mb-2">Datenschutz</h4>
                  <p className="text-sm text-muted-foreground mb-3">
                    E-Mail-Inhalte werden sicher in der Datenbank gespeichert.
                  </p>
                  <div className="flex items-center gap-2 text-green-600">
                    <CheckCircle className="h-4 w-4" />
                    <span className="text-sm">Geschützt</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Speichern Button */}
      <div className="flex justify-end gap-3 pt-6 border-t">
        <Button variant="outline" onClick={onClose}>
          Abbrechen
        </Button>
        <Button onClick={saveSettings} disabled={loading} className="gap-2">
          {loading ? (
            <RefreshCw className="h-4 w-4 animate-spin" />
          ) : (
            <CheckCircle className="h-4 w-4" />
          )}
          Einstellungen speichern
        </Button>
      </div>

      {/* Email Account Setup Modal */}
      <EmailAccountSetup 
        open={showAccountSetup} 
        onOpenChange={(open) => {
          setShowAccountSetup(open);
          if (!open) {
            setEditingAccount(null);
            loadEmailAccounts();
          }
        }}
        initialAccount={editingAccount}
      />
    </div>
  );
};